import { FC, useEffect } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

import { Pages } from "../../app/constants/routes";
import { generateRoutePath } from "../../app/constants/routes/utils";
import { NotFoundPanel } from "../../components/errors/NotFoundPanel";
import { LottieLoading } from "../../components/graphics/LottieLoading";
import { PageLayout } from "../../components/layouts/PageLayout";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useMarkWorkNotificationsReadMutation } from "../../slices/NotificationCenterSlice";
import { useGetWorkQuery } from "../../slices/WorkSlice";
import { CustomLink } from "../../support/CustomLink";
import { tabLabel } from "../../utils/tabCounts";
import { WorkClientRequests } from "../clientRequests/WorkClientRequests";
import { WorkTitle } from "./components/WorkTitle";
import { HIDE_AUTOMATION } from "./pages/automation/automation.constants";
import { HIDE_REMINDERS } from "./pages/reminders/reminders.mock";
import { useWorkTabCounts } from "./useWorkTabCounts";

export const WorkView: FC = () => {
  const { workId } = useParams();
  const [markWorkRead] = useMarkWorkNotificationsReadMutation();

  // Opening a work item marks your notifications about it as read.
  useEffect(() => {
    const orgId = getLinkedFirm()?.orgId;
    if (workId && orgId) markWorkRead({ orgId, workId }).catch(() => undefined);
  }, [workId, markWorkRead]);

  // currentData = the work of THIS address only: `data` kept showing the previously
  // opened work item when the new id didn't exist (QA SEC-001).
  const { currentData: work, isFetching: isFetchingWork } = useGetWorkQuery({
    workId: workId || "",
  });

  // Each tab's count reads the same data as the tab (Roadmap 1842).
  const counts = useWorkTabCounts(workId || "", work);

  const {
    details,
    time,
    notes,
    repeats,
    files,
    emails,
    sequences,
    automation,
    reminders,
    log,
  } = Pages.work.children;

  const DetailsComponent = details.component;
  const TimeComponent = time.component;
  const NotesComponent = notes.component;
  const RepeatsComponent = repeats.component;
  const FilesComponent = files.component;
  const EmailsComponent = emails.component;
  const SequencesComponent = sequences.component;
  const AutomationComponent = automation.component;
  const RemindersComponent = reminders.component;
  const LogComponent = log.component;

  if (!work && isFetchingWork) {
    return <LottieLoading />;
  }

  if (!work) {
    return (
      <NotFoundPanel
        title={"This work item doesn't exist or was deleted"}
        text={"The link may be old, or the work item was deleted."}
        linkTo={"/works/all"}
        linkLabel={"Back to Work"}
      />
    );
  }

  return (
    <div
      className={
        "flex h-full justify-center pl-4 pr-4 max-md:px-0 xl:pl-4 xl:pr-4"
      }>
      <div className={"h-full w-full"}>
        <PageLayout
          title={<WorkTitle work={work} />}
          extraClasses={"overflow-visible"}>
          <div className={"tabs my-4"}>
            <CustomLink
              to={generateRoutePath([
                Pages.work.path,
                workId || "",
                details.path,
              ])}>
              {details.label}
            </CustomLink>

            <CustomLink
              to={generateRoutePath([
                Pages.work.path,
                workId || "",
                time.path,
              ])}>
              {tabLabel(time.label, counts.time)}
            </CustomLink>

            <CustomLink
              to={generateRoutePath([
                Pages.work.path,
                workId || "",
                repeats.path,
              ])}>
              {tabLabel(repeats.label, counts.repeats)}
            </CustomLink>

            <CustomLink
              to={generateRoutePath([
                Pages.work.path,
                workId || "",
                files.path,
              ])}>
              {tabLabel(files.label, counts.files)}
            </CustomLink>

            <CustomLink
              to={generateRoutePath([
                Pages.work.path,
                workId || "",
                notes.path,
              ])}>
              {tabLabel(notes.label, counts.notes)}
            </CustomLink>

            <CustomLink
              to={generateRoutePath([
                Pages.work.path,
                workId || "",
                emails.path,
              ])}>
              {tabLabel(emails.label, counts.emails)}
            </CustomLink>

            <CustomLink
              to={generateRoutePath([
                Pages.work.path,
                workId || "",
                sequences.path,
              ])}>
              {tabLabel(sequences.label, counts.sequences)}
            </CustomLink>

            {/* Client request lists (Mg-232). */}
            <CustomLink
              to={generateRoutePath([
                Pages.work.path,
                workId || "",
                "requests",
              ])}>
              Client requests
            </CustomLink>

            {!HIDE_AUTOMATION && (
              <CustomLink
                to={generateRoutePath([
                  Pages.work.path,
                  workId || "",
                  automation.path,
                ])}>
                {tabLabel(automation.label, counts.automation)}
              </CustomLink>
            )}

            {!HIDE_REMINDERS && (
              <CustomLink
                to={generateRoutePath([
                  Pages.work.path,
                  workId || "",
                  reminders.path,
                ])}>
                {tabLabel(reminders.label, counts.reminders)}
              </CustomLink>
            )}

            <CustomLink
              to={generateRoutePath([Pages.work.path, workId || "", log.path])}>
              {tabLabel(log.label, counts.log)}
            </CustomLink>
          </div>

          <Routes>
            <Route
              index
              element={<Navigate to={"./" + details.path} replace={true} />}
            />

            <Route
              path={details.path}
              element={<DetailsComponent work={work} />}
            />

            <Route path={time.path} element={<TimeComponent work={work} />} />

            <Route path={repeats.path} element={<RepeatsComponent />} />

            <Route path={files.path} element={<FilesComponent />} />

            <Route path={notes.path} element={<NotesComponent />} />

            <Route path={emails.path} element={<EmailsComponent />} />

            <Route path={sequences.path} element={<SequencesComponent />} />

            <Route path={"requests"} element={<WorkClientRequests />} />

            {!HIDE_AUTOMATION && (
              <Route
                path={automation.path}
                element={<AutomationComponent work={work} />}
              />
            )}

            {!HIDE_REMINDERS && (
              <Route
                path={reminders.path}
                element={<RemindersComponent work={work} />}
              />
            )}

            <Route path={log.path} element={<LogComponent />} />
          </Routes>
        </PageLayout>
      </div>
    </div>
  );
};
